import { Dispatch, FC, SetStateAction, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { TLegacyDriver } from "../../api/types/Driver";
import { VehicleMapDataPoint } from "../../api/types/VehicleMap";
import dashboardMenuIcons from "../../assets/svg/dashboardMenuIcons";
import HeaderView from "../map-asset/headerView";
import TripDetails from "../map-asset/tripDetails";
import { VehicleGroup } from "./useMultiVehicleTripData";
import {
  tripDisplayNumberOr,
  tripDisplayNumbers,
} from "../map-asset/multiDay/tripNumbering";

type Props = {
  vehicleGroups: VehicleGroup[];
  // one trip, or every selected trip for an asset
  tripKeys: string[];
  // null means every trip in the set is drawn equally; setting one drops
  // the others back so a single route can be read on its own
  activeTripKey: string | null;
  onSetActiveTripKey: (tripKey: string | null) => void;
  onHoverTrip: (tripKey: string | null) => void;
  assignedDriver?: TLegacyDriver;
  selectedTripPoint: VehicleMapDataPoint | null;
  setSelectedTripPoint: Dispatch<SetStateAction<VehicleMapDataPoint | null>>;
  onBack: () => void;
  onReplayEvents: (tripKey: string) => void;
  replayingTripKey: string | null;
  replayPercentage: number | null;
};

// takes over the left panel with the open trips' full detail, reached from a
// list row, a vehicle header, or by clicking a route on the map
export const VehicleTripDetailPanel: FC<Props> = ({
  vehicleGroups,
  tripKeys,
  activeTripKey,
  onSetActiveTripKey,
  onHoverTrip,
  assignedDriver,
  selectedTripPoint,
  setSelectedTripPoint,
  onBack,
  onReplayEvents,
  replayingTripKey,
  replayPercentage,
}) => {
  const { t } = useTranslation("translation", { keyPrefix: "vehicleMap" });

  const trips = useMemo(
    () =>
      tripKeys
        .map((tripKey) => {
          const [deviceId, tripIndexKey] = tripKey.split("__");
          const group = vehicleGroups.find(
            (item) => item.deviceId === deviceId
          );
          const tripIndex = Number(tripIndexKey);
          const points = group?.trips[tripIndex];
          if (!group || !points) return null;
          return { tripKey, group, tripIndex, points };
        })
        .filter((item): item is NonNullable<typeof item> => !!item),
    [tripKeys, vehicleGroups]
  );

  // every trip in the set belongs to one asset unless it was hand-picked
  const vehicles = Array.from(
    new Set(trips.map((trip) => trip.group.fullLabel))
  );

  if (trips.length === 0) {
    return (
      <>
        <HeaderView
          title={t("tripDetails.heading")}
          showBackButton
          backButtonCallback={onBack}
          wrapperClassName="flex justify-between px-4 py-2 mt-4"
        />
        <div className="px-4 py-6 text-xs text-heading-gray888">
          These trips are no longer available.
        </div>
      </>
    );
  }

  return (
    <div className="flex-grow min-h-0 flex flex-col overflow-y-auto overscroll-contain">
      <HeaderView
        title={t("tripDetails.heading")}
        showBackButton
        backButtonCallback={onBack}
        wrapperClassName="flex justify-between px-4 py-2 mt-4 shrink-0"
      />

      <div className="mt-2 mx-3 flex items-start text-accent-blue-dark shrink-0">
        <img
          className="bg-accent-blue-pale rounded-lg px-2 size-10"
          src={dashboardMenuIcons.admins.vehicle}
          alt=""
        />
        <div className="flex flex-col gap-1 ms-4">
          <h3 className="font-bold text-base leading-4">
            {vehicles.join(" | ")}
          </h3>
          <span className="text-xs text-heading-gray888">
            {trips.length} trip{trips.length === 1 ? "" : "s"} on the map
          </span>
        </div>
      </div>

      {trips.length > 1 && (
        <div className="flex items-center justify-between px-4 py-2 mt-1 shrink-0 border-t border-gray-100">
          <span className="text-xs text-heading-gray888">
            {activeTripKey
              ? "Showing one trip"
              : "Showing all trips - pick one to isolate it"}
          </span>
          {activeTripKey && (
            <button
              type="button"
              onClick={() => onSetActiveTripKey(null)}
              className="text-xs font-medium text-accent-blue-bright hover:underline shrink-0"
            >
              Show all
            </button>
          )}
        </div>
      )}

      <div>
        {trips.map(({ tripKey, group, tripIndex, points }) => (
          <div key={tripKey}>
            {trips.length > 1 && (
              <button
                type="button"
                onClick={() =>
                  onSetActiveTripKey(activeTripKey === tripKey ? null : tripKey)
                }
                onMouseOver={() => onHoverTrip(tripKey)}
                onMouseOut={() => onHoverTrip(null)}
                className={`w-full flex items-center gap-2 px-4 py-2 text-left border-t border-gray-100 ${
                  activeTripKey === tripKey
                    ? "bg-accent-blue-paleO66"
                    : "hover:bg-accent-blue-paleO66"
                }`}
              >
                <span
                  className="inline-block size-3 rounded-full shrink-0"
                  style={{ backgroundColor: group.color }}
                />
                <span className="text-sm font-semibold flex-grow">
                  {group.fullLabel} &middot; Trip {tripIndex + 1}
                </span>
                <span className="text-xs text-heading-gray888 shrink-0">
                  {activeTripKey === tripKey ? "Hide" : "Isolate"}
                </span>
              </button>
            )}

            {(trips.length === 1 || activeTripKey === tripKey) && (
              <TripDetails
                assetLabel={group.fullLabel}
                assignedDriver={assignedDriver}
                tripNumber={tripDisplayNumberOr(
                  tripDisplayNumbers(group.trips),
                  tripIndex
                )}
                tripPoints={points}
                selectedTripPoint={selectedTripPoint}
                setSelectedTripPoint={setSelectedTripPoint}
                colorOverride={group.color}
                detailedView
                onClick={() => {}}
                onReplayEvents={() => onReplayEvents(tripKey)}
                replayInProgress={replayingTripKey !== null}
                replayPercentage={
                  replayingTripKey === tripKey ? replayPercentage : null
                }
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
